import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { TextInput, RadioTiles, Checkbox } from "@/components/quote/Fields";
import { CONTACT_METHODS } from "@/lib/quoteOptions";

function payloadFromData(data) {
  const y = parseInt(data.vehicle.year, 10);
  return {
    vehicle: {
      registration: data.vehicle.registration || "",
      make: data.vehicle.make,
      model: data.vehicle.model,
      year: Number.isNaN(y) ? null : y,
      fuel: data.vehicle.fuel || "",
      body_type: data.vehicle.body_type || "",
      vehicle_size: data.vehicle.vehicle_size,
    },
    condition: data.condition,
    lowered: data.lowered,
    journey: {
      pickup_location: data.journey.pickup_location,
      dropoff_location: data.journey.dropoff_location,
      miles: Number(data.journey.miles || 0),
    },
    collection: {
      date: data.collection.date,
      time_slot: data.collection.time_slot,
      flexibility: data.collection.flexibility || "none",
    },
    access: data.access,
    auction: data.auction,
  };
}

const money = (n) => `£${Number(n).toLocaleString("en-GB")}`;

export default function StepBooking({ data, update, preview, onBack }) {
  const c = data.customer;
  const setC = (k, v) => update({ customer: { ...c, [k]: v } });
  const [submitting, setSubmitting] = useState(false);
  const [paymentMode, setPaymentMode] = useState("deposit"); // "deposit" | "full"

  const manual = !!preview?.manual_quote_required;
  const journey = `${data.journey.pickup_location} → ${data.journey.dropoff_location}`;
  const veh = `${data.vehicle.year || ""} ${data.vehicle.make} ${data.vehicle.model}`.trim();

  const canSubmit =
    c.name.trim() &&
    c.phone.trim() &&
    c.email.trim() &&
    (manual || data.terms_accepted);

  async function submitManual() {
    setSubmitting(true);
    try {
      await publicApi.post("/leads", {
        quote: payloadFromData(data),
        customer: {
          name: c.name,
          phone: c.phone,
          email: c.email,
          preferred_contact: c.preferred_contact,
        },
        notes: data.notes,
        reasons: preview?.manual_reasons || [],
      });
      toast.success("Details received — we’ll be in touch shortly.");
      // Simple thank-you inline
      update({ __lead_submitted: true });
    } catch (e) {
      console.error(e);
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitBooking() {
    if (!data.terms_accepted) {
      toast.error("Please accept the Terms & Conditions to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await publicApi.post("/bookings", {
        quote: payloadFromData(data),
        customer: {
          name: c.name,
          phone: c.phone,
          email: c.email,
          preferred_contact: c.preferred_contact,
        },
        notes: data.notes,
        marketing_consent: !!data.marketing_consent,
        terms_accepted: !!data.terms_accepted,
        origin_url: window.location.origin,
        payment_mode: paymentMode,
      });
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      toast.error("Payment couldn’t be started. Please try again.");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Could not create booking.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }

  if (data.__lead_submitted) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="lead-success">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
          Received
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl">
          Thanks — we’re on it.
        </h1>
        <p className="text-zinc-400">
          Your details have been sent. Our team will contact you with your
          tailored quote shortly.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="step-booking"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-8"
    >
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
          {manual ? "Custom Quote · Your details" : "Step 05 · Secure Booking"}
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
          {manual ? "Send us your details" : "Almost there."}
        </h1>
      </div>

      {/* Summary card for booking flow */}
      {!manual && preview && (
        <div className="border border-white/10 bg-[#0A0A0A] p-6 space-y-3" data-testid="booking-summary">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Your Booking</div>
          <div className="text-white text-xl md:text-2xl font-display font-bold tracking-tight">
            {journey}
          </div>
          <div className="text-zinc-400 text-sm">{veh}</div>
          <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-white/10 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1">
                Transport
              </div>
              <div className="text-white font-bold text-lg">{money(preview.instant_booking_price)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1">
                Deposit today
              </div>
              <div className="text-[#E60000] font-bold text-lg">{money(preview.deposit_due)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1">
                Balance
              </div>
              <div className="text-white font-bold text-lg">{money(preview.balance_remaining)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Payment mode selector */}
      {!manual && preview && (
        <div data-testid="booking-payment-mode" className="space-y-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
            Choose how you&rsquo;d like to pay
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="booking-mode-deposit"
              onClick={() => setPaymentMode("deposit")}
              className={`text-left border p-4 transition-colors ${
                paymentMode === "deposit"
                  ? "border-[#E60000] bg-[#E60000]/5"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                  Book Now &mdash; Pay Deposit
                </div>
                {paymentMode === "deposit" && <div className="w-2 h-2 rounded-full bg-[#E60000]" />}
              </div>
              <div className="mt-1 text-white font-bold text-sm">Card · Apple Pay · Google Pay</div>
              <div className="mt-2 font-display text-2xl font-extrabold text-white">{money(preview.deposit_due)}</div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Balance {money(preview.balance_remaining)} due on delivery
              </div>
            </button>
            <button
              type="button"
              data-testid="booking-mode-full"
              onClick={() => setPaymentMode("full")}
              className={`text-left border p-4 transition-colors ${
                paymentMode === "full"
                  ? "border-[#E60000] bg-[#E60000]/5"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                  Pay Full Amount &mdash; Card or Klarna
                </div>
                {paymentMode === "full" && <div className="w-2 h-2 rounded-full bg-[#E60000]" />}
              </div>
              <div className="mt-1 text-white font-bold text-sm">Card · Apple Pay · Google Pay · Klarna</div>
              <div className="mt-2 font-display text-2xl font-extrabold text-white">{money(preview.instant_booking_price)}</div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Nothing more to pay on delivery
              </div>
              <KlarnaHint amount={preview.instant_booking_price} />
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <TextInput
          label="Full name *"
          testId="booking-name"
          value={c.name}
          onChange={(e) => setC("name", e.target.value)}
        />
        <TextInput
          label="Mobile number *"
          testId="booking-phone"
          type="tel"
          value={c.phone}
          onChange={(e) => setC("phone", e.target.value)}
        />
      </div>
      <TextInput
        label="Email *"
        testId="booking-email"
        type="email"
        value={c.email}
        onChange={(e) => setC("email", e.target.value)}
      />
      <RadioTiles
        label="Preferred contact"
        testId="booking-contact"
        value={c.preferred_contact}
        onChange={(v) => setC("preferred_contact", v)}
        options={CONTACT_METHODS}
        columns={3}
      />

      {!manual && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
          <Checkbox
            id="terms"
            testId="booking-terms"
            label={
              <>
                I have read and agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-white hover:text-[#E60000]"
                  data-testid="booking-terms-link"
                >
                  Terms &amp; Conditions
                </a>
                .
              </>
            }
            checked={!!data.terms_accepted}
            onChange={(v) => update({ terms_accepted: v })}
          />
          <Checkbox
            id="marketing"
            testId="booking-marketing"
            label="I'd like to hear about future offers and service updates (optional)."
            checked={!!data.marketing_consent}
            onChange={(v) => update({ marketing_consent: v })}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-2">
        <button type="button" onClick={onBack} className="btn-ghost">
          Back
        </button>
        <button
          type="button"
          onClick={manual ? submitManual : submitBooking}
          disabled={!canSubmit || submitting}
          data-testid={manual ? "lead-submit" : "booking-pay"}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Working…
            </>
          ) : manual ? (
            <>Send request</>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5" />
              {paymentMode === "full"
                ? `Pay ${money(preview?.instant_booking_price || 0)} in full`
                : `Pay ${money(preview?.deposit_due || 0)} deposit`}
            </>
          )}
        </button>
      </div>
      {!manual && (
        <p className="text-[11px] text-zinc-500 max-w-2xl">
          Your deposit secures the booking. If MHJ Prestige Transport is
          unable to secure or fulfil the requested transport, the deposit
          will be refunded in full. Customer cancellations are subject to
          our Terms &amp; Conditions.
        </p>
      )}
    </motion.div>
  );
}

function KlarnaHint({ amount }) {
  const value = Number(amount || 0);
  if (!value || value <= 0) return null;
  // Klarna UK: Pay in 3 (interest-free) up to £1000; financing above.
  if (value <= 1000) {
    const per = (value / 3).toFixed(2);
    return (
      <div
        data-testid="klarna-hint"
        className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 border-t border-white/10 pt-3"
      >
        <KlarnaPill />
        <span>
          or 3 interest-free payments of <span className="text-white font-bold">£{per}</span> with Klarna
        </span>
      </div>
    );
  }
  return (
    <div
      data-testid="klarna-hint"
      className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 border-t border-white/10 pt-3"
    >
      <KlarnaPill />
      <span>Split into flexible monthly payments with Klarna</span>
    </div>
  );
}

function KlarnaPill() {
  return (
    <span
      aria-label="Klarna"
      className="inline-flex items-center justify-center rounded-sm bg-[#FFA8CD] text-black text-[9px] font-black tracking-wide px-1.5 py-0.5 leading-none"
      style={{ fontFamily: "Helvetica, Arial, sans-serif" }}
    >
      Klarna.
    </span>
  );
}
